import { action, computed, makeObservable, observable } from 'mobx';
import { nanoid } from 'nanoid';

import { equalsDeep } from 'src/packages/utils/equals-deep';
import { EventEmitter } from 'src/packages/utils/event-emitter';
import { getRandomNumber } from 'src/packages/utils/get-random-number';
import { hasValue } from 'src/packages/utils/has-value';
import { ControlRulesService } from 'src/services/control-rules-service/control-rules-service';
import { serializeDirectoryRecordToSave } from 'src/services/directory-service/directory-service.utils';
import { getControlsViews } from 'src/services/directory-service/mappers/get-controls-views';
import { ValidationService } from 'src/services/directory-service/validation-service/validation-service';
import { TableSettingsManager } from 'src/services/table-settings-service/table-settings-manager';

import type { IMainServices } from 'src/app/types';
import type { DirectoryTableMediator } from 'src/components/directory-table/directory-table-mediator';
import type { IDirectoriesStorage } from 'src/services/directories-storage-service/directories-storage-service';
import type { TMainDirectoryEvents } from 'src/services/directory-service/main-directory-service';
import type { OptionsService } from 'src/services/directory-service/options-service';
import type {
  IControl,
  ITableDirectoryService,
  TControlView,
  TSerializedRecord,
  TTableControlView,
} from 'src/services/directory-service/types';
import type { LocalizationService } from 'src/services/localization-service.ts';
import type { INotificationsService } from 'src/services/notifications-service';
import type { TableSettingsService } from 'src/services/table-settings-service/table-settings-service';

import { DirectoryRecord } from '../../directory-record.entity';

const executeOnce = (clb: VoidFunction) => {
  let isFired = false;

  return () => {
    if (isFired) {
      return;
    }

    isFired = true;
    clb();
  };
};

type ITableControl = IControl<Record<string | number, unknown>[], TTableControlView> &
  Omit<ITableDirectoryService<TTableControlView>, 'directoryName' | 'loadAdditionalDirectory'>;

export class TableControl {
  readonly id;
  readonly entityId;
  readonly view: TTableControlView;
  readonly fieldId: string;
  readonly attrName: string | null;
  readonly attrKey: string | null;
  readonly initialValue: Record<string | number, unknown>[];

  private readonly mainServices: IMainServices;
  readonly localizationService: LocalizationService;
  readonly notificationsService: INotificationsService;
  readonly directoriesStorageService: IDirectoriesStorage;
  readonly tableSettingsService: TableSettingsService;
  readonly optionsService: OptionsService;
  readonly validationService: ValidationService;
  readonly controlRulesService: ControlRulesService;
  readonly eventEmitter: EventEmitter<TMainDirectoryEvents> = new EventEmitter();

  @observable isEnteringBlocked = false;
  @observable isDisabled = false;
  @observable isLoading = false;
  @observable isRequired = false;
  @observable error: string | null = null;

  @observable.shallow records: DirectoryRecord[] = [];
  private _recordsMap = new Map<number | string, DirectoryRecord>();

  recordControlsViews: TControlView[] = [];
  generalControlsViews: TControlView[] = [];
  controlsViewsMap = new Map<string, TControlView>();

  @observable private generalData: Record<string, unknown> = {};

  @observable isDataFetching = false;
  @observable isImporting = false;
  @observable tableSettingsManager: TableSettingsManager;
  @observable directoryViewMediator: DirectoryTableMediator | null = null;
  readonly isImportAvailable: boolean;

  @observable.ref status:
    | { type: 'initial' }
    | { type: 'ready' }
    | { type: 'error'; message: 'generalControlsNotFilledIn' | 'noData' } = { type: 'initial' };

  constructor(
    mainServices: IMainServices,
    tableSettingsService: TableSettingsService,
    view: TTableControlView,
    externalOptionService: OptionsService,
    initialRecords: Record<string | number, unknown>[],
  ) {
    const id = nanoid(8);
    this.id = id;
    this.entityId = id;

    this.mainServices = mainServices;
    this.tableSettingsService = tableSettingsService;
    this.localizationService = mainServices.localizationService;
    this.directoriesStorageService = mainServices.directoriesStorage;
    this.optionsService = externalOptionService;
    this.validationService = new ValidationService(mainServices.localizationService);
    this.notificationsService = mainServices.notificationsService;
    this.view = view;
    this.tableSettingsManager = new TableSettingsManager(tableSettingsService, this.view.attrName, this);
    this.controlRulesService = new ControlRulesService(mainServices.directoriesStorage, this.validationService);
    this.isImportAvailable = !!view.allowImport;

    this.initializeControls();
    this.createRecords(initialRecords);
    this.initialValue = initialRecords;

    makeObservable(this);
  }

  @computed
  get generalDataValues(): Record<string, unknown> {
    return this.generalData;
  }

  @action.bound
  setValue(value: Record<string | number, unknown>[]): void {
    this._recordsMap.clear();
    this.records = [];

    this.createRecords(value);
  }

  @action.bound
  setIsRequired(isRequired: boolean): void {
    this.isRequired = isRequired;
  }

  @action.bound
  setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  @action.bound
  setError(error: string | null): void {
    this.error = error;
  }

  @action.bound
  setIsDisabled(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  @action.bound
  setIsEnteringBlocked(isBlocked: boolean): void {
    this.isEnteringBlocked = isBlocked;
  }

  get recordsMap(): Map<number | string, DirectoryRecord> {
    return this._recordsMap;
  }

  getRecordById(id: number): DirectoryRecord | null {
    const record = this._recordsMap.get(id) ?? null;

    return record;
  }

  get value(): TSerializedRecord[] {
    return this.records.map((r) => serializeDirectoryRecordToSave(r, this.recordControlsViews));
  }

  @computed
  get hasChanges(): boolean {
    return this.records.some((record) => record.recordChangeState);
  }

  @computed
  get newRecords(): DirectoryRecord[] {
    return this.records.filter((record) => record.recordChangeState === 'new');
  }

  @computed
  get updatedRecords(): DirectoryRecord[] {
    return this.records.filter((record) => record.recordChangeState === 'hasChanges');
  }

  @action.bound
  onControlBlur(record: DirectoryRecord, fieldId: string): void {
    this.validationService.validateField(record, fieldId);
  }

  @action.bound
  onValueChange(record: DirectoryRecord, fieldId: string, value: unknown): void {
    if (equalsDeep(record.data[fieldId], value)) {
      return;
    }

    const { [fieldId]: fieldErrors, ...rest } = record.fieldsErrors;
    record.updateFieldsErrors(rest);
    record.updateData({ [fieldId]: value });

    this.controlRulesService.onControlValueChange(
      record,
      this.controlsViewsMap,
      fieldId,
      value,
      (record: DirectoryRecord, fieldId: string, value: unknown) => {
        this.onValueChange(record, fieldId, value);
      },
    );

    this.eventEmitter.emit('recordDataUpdated', { record, fieldId });
  }

  @action.bound
  initializeControls() {
    const { recordControls } = getControlsViews(this.view);

    this.recordControlsViews = recordControls;
    this.controlRulesService.mapRulesFromControlViews(recordControls);

    recordControls.forEach((controlView) => {
      this.controlsViewsMap.set(controlView.fieldId, controlView);

      const localeKey = `labels:${controlView.fieldId}.label`;
      const defaultLabel = controlView.defaultLabel || controlView.defaultValue;

      if (!this.localizationService.i18n.exists(localeKey) && defaultLabel) {
        this.localizationService.i18n.languages.forEach((lang) => {
          this.localizationService.i18n.addResource(lang, 'labels', `${controlView.fieldId}.label`, defaultLabel);
        });
      }
    });

    this.validationService.setControlViews(recordControls);
    this.validationService.setRequiredFields(this.view.required);

    this.status = { type: 'ready' };
  }

  @action.bound
  discardChanges(): void {
    const initialRecords: DirectoryRecord[] = [];

    for (const record of this.records) {
      record.updateFieldsErrors({});

      if (record.recordChangeState === 'new') {
        continue;
      }

      if (record.recordChangeState === 'hasChanges') {
        for (const fieldId in record.data) {
          const initialValue = record.initialData?.[fieldId];

          record.updateData({ ...record.data, [fieldId]: initialValue });
          this.onValueChange(record, fieldId, initialValue);
        }

        initialRecords.push(record);
        continue;
      }

      initialRecords.push(record);
    }

    this.records = initialRecords;
  }

  @action.bound
  resetRecord(record: DirectoryRecord): void {
    if (record.recordChangeState !== 'hasChanges') {
      console.error('deleting record has no changes');
      return;
    }

    for (const fieldView of this.recordControlsViews) {
      if (!fieldView.attrName) {
        continue;
      }

      const initialValue = record.initialData?.[fieldView.fieldId];

      this.onValueChange(record, fieldView.fieldId, initialValue);
    }
  }

  @action.bound
  deleteRecord(record: DirectoryRecord): void {
    if (record.recordChangeState !== 'new') {
      console.error('deleting record is not new');
      return;
    }
    const index = this.records.indexOf(record);
    if (index >= 0) {
      this.records.splice(index, 1);
      this._recordsMap.delete(record.recordId);
      this.directoryViewMediator?.onDeleteRecord?.(record);
    }
  }

  @action.bound
  createRecords(initialValues: Record<string, unknown>[]): DirectoryRecord[] {
    const records: DirectoryRecord[] = [];
    const recordsMap = new Map<number | string, DirectoryRecord>();

    const requiredRecords = this.validationService.requiredFieldNames;
    const controlViews = this.recordControlsViews;

    const idFieldName = this.view.rowIdKey ?? 'id';

    initialValues.forEach((initRecord) => {
      const id = initRecord?.[idFieldName] ?? -getRandomNumber();

      if (!hasValue(id) || (typeof id !== 'number' && typeof id !== 'string')) {
        console.error('wring type of record id');
        return;
      }

      const newRecord = new DirectoryRecord(controlViews, initRecord, id);

      this.recordControlsViews.forEach((v) => {
        this.controlRulesService.onControlValueChange(
          newRecord,
          this.controlsViewsMap,
          v.fieldId,
          initRecord[v.fieldId],
          (record: DirectoryRecord, fieldId: string, value: unknown) => {
            this.onValueChange(record, fieldId, value);
          },
        );
      });

      records.push(newRecord);
      recordsMap.set(id, newRecord);
    });

    Array.from(recordsMap.entries()).forEach(([id, record]) => this._recordsMap.set(id, record));

    for (const newRecord of records) {
      this.records.push(newRecord);
    }

    return records;
  }

  @action.bound
  addNewRecord(initialValue?: Record<string, unknown>): DirectoryRecord {
    const idFieldName = this.view.rowIdKey ?? 'id';

    const id = initialValue?.[idFieldName] ?? -getRandomNumber();

    if (!hasValue(id) || (typeof id !== 'number' && typeof id !== 'string')) {
      throw new Error('wring type of record id');
    }

    const controlViews = this.recordControlsViews;

    const newRecord = new DirectoryRecord(controlViews, initialValue ?? null, id);
    this.records.push(newRecord);

    this.directoryViewMediator?.onAddNewRecord?.(newRecord);

    this._recordsMap.set(id, newRecord);

    controlViews.forEach((v) => {
      const savedValue = (() => {
        if (!initialValue) {
          return null;
        }

        if (v.attrName && hasValue(initialValue[v.attrName])) {
          return initialValue[v.attrName];
        }

        if (hasValue(initialValue[v.fieldId])) {
          return initialValue[v.fieldId];
        }

        return null;
      })();

      if (!hasValue(savedValue)) {
        return;
      }

      this.onValueChange(newRecord, v.fieldId, savedValue);
    });

    return newRecord;
  }

  @action.bound
  setDirectoryViewMediator(mediator: DirectoryTableMediator | null): void {
    this.directoryViewMediator = mediator;
  }

  init = (): void => {
    // this.tableSettingsManager.checkSettings(this.controlsViewsMap);
  };
}
